import { useState, useEffect } from "react";
import { AlertCircle } from "lucide-react";
import { toast } from "react-toastify";
import { getHelpCenterAnalytics } from "./supportApi";

const WINDOWS = [7, 30, 90];
const ROW_LIMIT = 10;

/**
 * Rates come back as null, not zero, when nothing happened in the window. 0% would read as
 * "everything failed", which is a different claim to "nobody asked".
 */
const formatRate = (rate) =>
  rate === null || rate === undefined ? "—" : `${Math.round(rate * 100)}%`;

const formatDate = (date) =>
  date ? new Date(date).toLocaleDateString("en-US") : "N/A";

const formatDay = (day) =>
  new Date(day).toLocaleDateString("en-US", { month: "short", day: "numeric" });

/** The title is null when the article has since been deleted — the views still happened. */
const titleOf = (row) => row.title || "Deleted article";

function StatCard({ label, value, hint }) {
  return (
    <div className="rounded-lg border border-border/70 bg-card p-4">
      <p className="text-[10px] font-medium uppercase tracking-[0.12em] text-muted-foreground">
        {label}
      </p>
      <p className="mt-1.5 font-mono text-2xl tabular-nums text-foreground">{value}</p>
      {hint && <p className="mt-0.5 text-[10px] text-muted-foreground">{hint}</p>}
    </div>
  );
}

function Panel({ title, subtitle, children }) {
  return (
    <div className="rounded-lg border border-border/70 bg-card">
      <div className="border-b border-border/70 px-4 py-3">
        <p className="text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">
          {title}
        </p>
        {subtitle && (
          <p className="mt-0.5 text-[10px] text-muted-foreground/70">{subtitle}</p>
        )}
      </div>
      {children}
    </div>
  );
}

function Empty({ children }) {
  return <p className="px-4 py-6 text-xs text-muted-foreground">{children}</p>;
}

/** A query can be 200 characters of someone leaning on a key — never let it widen the page. */
function QueryRows({ rows, showAverage }) {
  if (rows.length === 0) return <Empty>Nothing in this window.</Empty>;

  return (
    <div className="divide-y divide-border/40">
      {rows.map((row) => (
        <div key={row.query} className="flex items-baseline gap-3 px-4 py-2.5">
          <span
            title={row.query}
            className="min-w-0 flex-1 truncate text-sm text-foreground"
          >
            {row.query}
          </span>
          {showAverage && (
            <span className="shrink-0 font-mono text-[10px] tabular-nums text-muted-foreground/70">
              {row.averageResults} results
            </span>
          )}
          <span className="shrink-0 font-mono text-xs tabular-nums text-muted-foreground">
            {row.searches}
          </span>
        </div>
      ))}
    </div>
  );
}

export default function HelpCenterAnalytics() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setIsLoading(true);
      try {
        setData(await getHelpCenterAnalytics(days, ROW_LIMIT));
      } catch (error) {
        console.error("Error loading analytics:", error);
        toast.error("Failed to load the analytics");
        setData(null);
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, [days]);

  const search = data?.search;
  const articles = data?.articles;
  const ai = data?.ai;
  const overTime = ai?.overTime || [];
  // One scale across every day, so a tall bar means a busy day rather than a full row.
  const busiestDay = Math.max(
    1,
    ...overTime.map((d) => d.answered + d.escalated),
  );
  const escalationReasons = Object.entries(ai?.escalationReasons || {});

  return (
    <div className="">
      {/* Page header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground mb-1">
          <span className="h-px w-6 bg-ginox-green" aria-hidden="true" />
          Overview
        </div>
        <h1 className="font-serif italic text-[36px] leading-[1.05] text-foreground mb-1">
          Help Center <span className="text-gradient-ginox">Analytics</span>
        </h1>
        <p className="text-sm text-muted-foreground mb-4">
          What people searched for, what they read, and how often the bot answered
          instead of handing over.
        </p>
      </div>

      <div className="mb-6 flex flex-wrap items-center gap-3">
        <div className="flex gap-2">
          {WINDOWS.map((option) => (
            <button
              key={option}
              onClick={() => setDays(option)}
              className={
                days === option
                  ? "rounded-md border border-ginox-green/60 bg-ginox-green/10 px-3 py-1.5 text-xs font-medium text-ginox-green"
                  : "rounded-md border border-border/70 bg-white/[0.03] px-3 py-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
              }
            >
              {option} days
            </button>
          ))}
        </div>
        {/* Event tracking has a start date. Without this, a flat first period reads as a
            trend rather than as the days before anything was being recorded. */}
        {data?.window && (
          <p className="text-[10px] text-muted-foreground">
            Counting from {formatDate(data.window.since)}
          </p>
        )}
      </div>

      {isLoading ? (
        <div className="py-10 flex justify-center">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-ginox-green" />
        </div>
      ) : !data ? (
        <div className="flex items-start gap-2 p-4 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
          <AlertCircle
            className="h-4 w-4 text-yellow-400 mt-0.5 shrink-0"
            aria-hidden="true"
          />
          <p className="text-yellow-400 text-sm">
            The analytics could not be loaded. Try again in a moment.
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
            <StatCard label="Searches" value={search.total} />
            <StatCard
              label="Found nothing"
              value={search.zeroResult}
              hint={`${formatRate(search.zeroResultRate)} of searches`}
            />
            <StatCard label="AI answered" value={ai.answered} />
            <StatCard label="Escalated" value={ai.escalated} />
            <StatCard label="Answer rate" value={formatRate(ai.answerRate)} />
          </div>

          {/* Deliberately first: every row here is a question the help center could not
              answer, which is the one list on this page worth acting on. */}
          <Panel
            title="Searches that found nothing"
            subtitle="Each of these is a gap in the knowledge base."
          >
            <QueryRows rows={search.zeroResultQueries} showAverage={false} />
          </Panel>

          <div className="grid gap-6 lg:grid-cols-2">
            <Panel title="Top searches">
              <QueryRows rows={search.topQueries} showAverage />
            </Panel>

            <Panel title="Most read articles">
              {articles.mostViewed.length === 0 ? (
                <Empty>No article was opened in this window.</Empty>
              ) : (
                <div className="divide-y divide-border/40">
                  {articles.mostViewed.map((row) => (
                    <div
                      key={row.articleId}
                      className="flex items-baseline gap-3 px-4 py-2.5"
                    >
                      <span className="min-w-0 flex-1 truncate text-sm text-foreground">
                        {titleOf(row)}
                      </span>
                      <span className="shrink-0 font-mono text-xs tabular-nums text-muted-foreground">
                        {row.views}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </Panel>
          </div>

          <div className="grid gap-6 lg:grid-cols-2">
            {/* Not windowed, and the backend means it that way: a verdict on an article is
                not a fact about the week it was cast in. */}
            <Panel
              title="Worst rated articles"
              subtitle="All time, not just this window."
            >
              {articles.worstRated.length === 0 ? (
                <Empty>No article has been marked unhelpful.</Empty>
              ) : (
                <div className="divide-y divide-border/40">
                  {articles.worstRated.map((row) => (
                    <div
                      key={row.articleId}
                      className="flex items-baseline gap-3 px-4 py-2.5"
                    >
                      <span className="min-w-0 flex-1 truncate text-sm text-foreground">
                        {titleOf(row)}
                      </span>
                      <span className="shrink-0 font-mono text-[10px] tabular-nums">
                        <span className="text-ginox-green">{row.helpful}</span>
                        <span className="text-muted-foreground/50"> / </span>
                        <span className="text-red-400">{row.unhelpful}</span>
                      </span>
                      <span className="shrink-0 font-mono text-xs tabular-nums text-muted-foreground">
                        {formatRate(row.ratio)}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </Panel>

            <Panel
              title="AI, day by day"
              subtitle="Answered against escalated."
            >
              {overTime.length === 0 ? (
                <Empty>The bot was not asked anything in this window.</Empty>
              ) : (
                <div className="space-y-2 px-4 py-3">
                  {overTime.map((day) => {
                    const total = day.answered + day.escalated;
                    return (
                      <div key={day.day} className="flex items-center gap-3">
                        <span className="w-14 shrink-0 font-mono text-[10px] text-muted-foreground">
                          {formatDay(day.day)}
                        </span>
                        <div
                          className="flex h-2.5 flex-1 overflow-hidden rounded-sm bg-white/[0.04]"
                          style={{ maxWidth: `${(total / busiestDay) * 100}%` }}
                        >
                          <div
                            className="bg-ginox-green"
                            style={{ width: `${(day.answered / total) * 100}%` }}
                          />
                          <div
                            className="bg-amber-500"
                            style={{ width: `${(day.escalated / total) * 100}%` }}
                          />
                        </div>
                        <span className="shrink-0 font-mono text-[10px] tabular-nums text-muted-foreground">
                          {day.answered} / {day.escalated}
                        </span>
                      </div>
                    );
                  })}

                  <div className="flex gap-4 pt-2 text-[10px] text-muted-foreground">
                    <span className="flex items-center gap-1.5">
                      <span className="h-2 w-2 rounded-sm bg-ginox-green" />
                      answered
                    </span>
                    <span className="flex items-center gap-1.5">
                      <span className="h-2 w-2 rounded-sm bg-amber-500" />
                      escalated
                    </span>
                  </div>

                  {escalationReasons.length > 0 && (
                    <div className="border-t border-border/40 pt-2.5">
                      <p className="mb-1.5 text-[10px] font-medium uppercase tracking-[0.08em] text-muted-foreground">
                        Why it gave up
                      </p>
                      {escalationReasons.map(([reason, count]) => (
                        <div
                          key={reason}
                          className="flex items-baseline justify-between gap-3 py-0.5"
                        >
                          <span className="truncate text-[11px] text-muted-foreground">
                            {reason.replace(/_/g, " ")}
                          </span>
                          <span className="font-mono text-[11px] tabular-nums text-muted-foreground">
                            {count}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </Panel>
          </div>
        </div>
      )}
    </div>
  );
}
